import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { COUNT_IN_CLICKS } from '../melody-capture/click-track.js'
import { TAKE_TAIL_MS } from '../melody-capture/capture-take.js'

/**
 * One attempt's spans, in milliseconds - pure.
 *
 * The whole attempt runs on ONE unbroken pulse at the melody's own tempo: two knocks, the
 * reference, one silent beat, the take. That is why the reference and the take are each rounded up
 * to whole BARS - the silent beat then lands on a beat, and the take's beat 1 lands exactly where
 * the ear already expects it. It is also why the take's click carries no count-in of its own: the
 * knocks and the reference have established the pulse, and a second cue after the silent beat
 * would read as a new start rather than as bar 1 of the same thing.
 *
 * A capture take asks the user how many bars to record. A trainer attempt cannot: the answer is
 * the melody's own length, and asking would let a player record four bars of a two-bar melody and
 * be scored against silence.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.4
 */

export interface AttemptTimeline {
  /** Bars the melody occupies - the length of both the reference and the take. */
  bars: number
  /** Two woodblock knocks, at the melody's tempo. */
  countInMs: number
  referenceMs: number
  /** The one silent beat between what the player heard and what they are about to play. */
  gapMs: number
  takeMs: number
  /** Listening past the last bar line, so a note released after the beat is not truncated. */
  tailMs: number
}

export const attemptTimeline = (melody: Melody): AttemptTimeline => {
  const beat = MelodyLib.beatMs(melody.tempo.bpm)
  const barMs = MelodyLib.barBeats(melody.tempo.meter) * beat

  /*
   * `- GRID_TOLERANCE` so a melody that exactly fills its last bar is not pushed into an empty one
   * by float error. An empty melody still gets a bar: there is nothing to hear, but the player has
   * to be able to reach the end of the attempt.
   */
  const bars = Math.max(1, Math.ceil(MelodyLib.melodyDurationMs(melody) / barMs - MelodyLib.GRID_TOLERANCE))
  const spanMs = bars * barMs

  return {
    bars,
    countInMs: COUNT_IN_CLICKS * beat,
    referenceMs: spanMs,
    gapMs: beat,
    takeMs: spanMs,
    tailMs: TAKE_TAIL_MS,
  }
}
